/**
 * ContactsPanel — multi-contact list on the customer profile.
 *
 * Replaces the flat key_contact/key_contact_email rendering with a proper
 * list of Contact rows (crm.contacts), each with:
 *   - identity (name, title, email, phone, primary flag)
 *   - operational roles (Travel Manager / AP-Finance / IT-SSO / etc.)
 *   - sales role from HubSpot (Champion / Decision Maker / ...)
 *   - status (Active / Left Company / Bounced / Unsubscribed)
 *   - HubSpot provenance badge when source=hubspot
 *
 * Edit / add / delete are inline; champion-departure ripple is handled
 * server-side when status flips to LEFT_COMPANY on the primary.
 *
 * Capabilities: write:contact for edit/add; delete:contact for delete.
 * The existing Customer Master "key contact" panel remains as a *legacy*
 * read-only surface (it comes from Domo, not us); this panel is the new
 * source of truth.
 */

import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import {
  OPERATIONAL_ROLES,
  OPERATIONAL_ROLE_LABEL,
  SALES_ROLES,
  SALES_ROLE_LABEL,
  CONTACT_STATUSES,
  CONTACT_STATUS_LABEL,
  type OperationalRole,
  type SalesRole,
  type ContactStatus,
} from '@/features/crm/lib/contact-roles';

interface Contact {
  id: string;
  hubspotContactId: string | null;
  hubspotCanonicalVid: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  phone: string | null;
  preferredChannel: string | null;
  operationalRoles: string[];
  salesRole: string | null;
  isPrimary: boolean;
  isDecisionMaker: boolean;
  status: string;
  emailOptedOut: boolean;
  emailBounced: boolean;
  source: string;
  hubspotSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// How a contact actually wants to be reached. Free-text column, but we pin a
// practical vocabulary so it stays filterable/consistent. Order = rough
// "reachability" preference we see in practice (text beats email beats calls).
const CHANNELS = [
  'email',
  'phone',
  'text',
  'teams',
  'slack',
  'in_person',
] as const;
const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email',
  phone: 'Phone call',
  text: 'Text / SMS',
  teams: 'Microsoft Teams',
  slack: 'Slack',
  in_person: 'In person',
};

const STATUS_THEME: Record<
  string,
  { bg: string; color: string; border: string }
> = {
  active: {
    bg: 'rgba(19,122,79,0.12)',
    color: '#137A4F',
    border: 'rgba(19,122,79,0.4)',
  },
  left_company: {
    bg: 'rgba(178,43,43,0.12)',
    color: '#B22B2B',
    border: 'rgba(178,43,43,0.45)',
  },
  bounced: {
    bg: 'rgba(178,106,10,0.12)',
    color: '#B26A0A',
    border: 'rgba(178,106,10,0.4)',
  },
  unsubscribed: {
    bg: 'rgba(137,118,128,0.14)',
    color: '#4F4148',
    border: 'rgba(137,118,128,0.45)',
  },
  merged: {
    bg: 'rgba(110,75,156,0.14)',
    color: '#6E4B9C',
    border: 'rgba(110,75,156,0.45)',
  },
};

export function ContactsPanel({ customerId }: { customerId: string }) {
  const { can } = useMe();
  const canWrite = can('write:contact');
  const canDelete = can('delete:contact');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const d = await apiFetch<{ contacts: Contact[] }>(
        `/api/customers/${encodeURIComponent(customerId)}/contacts`,
      );
      setContacts(d.contacts);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Contacts ({contacts.length})</span>
        {canWrite && !adding && (
          <button
            className="btn btn-outline btn-sm"
            onClick={() => setAdding(true)}
          >
            + Add contact
          </button>
        )}
      </div>
      <div className="section-body">
        {error && (
          <div className="banner banner-error" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        {adding && canWrite && (
          <ContactEditor
            customerId={customerId}
            mode="create"
            onCancel={() => setAdding(false)}
            onSaved={() => {
              setAdding(false);
              load();
            }}
          />
        )}

        {loading && (
          <div
            style={{ fontSize: 'var(--fs-base)', color: 'var(--text-muted)' }}
          >
            Loading contacts…
          </div>
        )}

        {!loading && contacts.length === 0 && !adding && (
          <div
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            No contacts on file yet.{' '}
            {canWrite && 'Add one above, or wait for HubSpot to sync them in.'}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {contacts.map((c) =>
            editingId === c.id ? (
              <ContactEditor
                key={c.id}
                customerId={customerId}
                mode="edit"
                contact={c}
                onCancel={() => setEditingId(null)}
                onSaved={() => {
                  setEditingId(null);
                  load();
                }}
                onDelete={
                  canDelete
                    ? async () => {
                        if (
                          !confirm(
                            'Delete this contact? HubSpot will re-add on next sync if it still exists there.',
                          )
                        )
                          return;
                        try {
                          await apiFetch(
                            `/api/customers/${encodeURIComponent(customerId)}/contacts/${c.id}`,
                            {
                              method: 'DELETE',
                            },
                          );
                          setEditingId(null);
                          load();
                        } catch (e) {
                          alert(e instanceof Error ? e.message : String(e));
                        }
                      }
                    : undefined
                }
              />
            ) : (
              <ContactRow
                key={c.id}
                contact={c}
                canEdit={canWrite}
                onEdit={() => setEditingId(c.id)}
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function ContactRow({
  contact,
  canEdit,
  onEdit,
}: {
  contact: Contact;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const fullName =
    [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
    contact.email ||
    '(unnamed)';
  const theme = STATUS_THEME[contact.status] ?? STATUS_THEME.active;
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        border: contact.isPrimary
          ? '2px solid var(--accent)'
          : '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: contact.isPrimary
          ? 'var(--accent-light)'
          : 'var(--bg-card)',
        alignItems: 'flex-start',
      }}
    >
      <div style={avatarStyle(fullName, 44)}>{initialsOf(fullName)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 'var(--fs-md)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {fullName}
          </span>
          {contact.isPrimary && (
            <Chip bg="var(--accent)" color="#fff" border="var(--accent)">
              Primary
            </Chip>
          )}
          {contact.isDecisionMaker && (
            <Chip
              bg="hsl(var(--brand-400) / 0.16)"
              color="#8A4423"
              border="hsl(var(--brand-400) / 0.45)"
            >
              Decision Maker
            </Chip>
          )}
          <Chip bg={theme.bg} color={theme.color} border={theme.border}>
            {CONTACT_STATUS_LABEL[contact.status as ContactStatus] ??
              contact.status}
          </Chip>
          {contact.source === 'hubspot' && (
            <Chip
              bg="rgba(255,127,52,0.12)"
              color="#C24A1A"
              border="rgba(255,127,52,0.4)"
            >
              HubSpot
            </Chip>
          )}
          {contact.preferredChannel &&
            CHANNEL_LABEL[contact.preferredChannel] && (
              <Chip
                bg="rgba(110,75,156,0.12)"
                color="#6E4B9C"
                border="rgba(110,75,156,0.45)"
              >
                Prefers {CHANNEL_LABEL[contact.preferredChannel]}
              </Chip>
            )}
        </div>
        {contact.jobTitle && (
          <div
            style={{
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-secondary)',
              marginTop: 2,
            }}
          >
            {contact.jobTitle}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 'var(--fs-base)',
            marginTop: 4,
          }}
        >
          {contact.email && (
            <a className="link" href={`mailto:${contact.email}`}>
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <a
              className="link"
              href={`tel:${contact.phone.replace(/[^+0-9]/g, '')}`}
            >
              {contact.phone}
            </a>
          )}
        </div>
        {contact.operationalRoles.length > 0 && (
          <div
            style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}
          >
            {contact.operationalRoles.map((r) => (
              <Chip
                key={r}
                bg="rgba(14,125,154,0.12)"
                color="#0E7D9A"
                border="rgba(14,125,154,0.4)"
              >
                {OPERATIONAL_ROLE_LABEL[r as OperationalRole] ?? r}
              </Chip>
            ))}
          </div>
        )}
        {contact.salesRole && (
          <div
            style={{
              fontSize: 'var(--fs-xs)',
              color: 'var(--text-muted)',
              marginTop: 6,
            }}
          >
            Sales role:{' '}
            {SALES_ROLE_LABEL[contact.salesRole as SalesRole] ??
              contact.salesRole}
          </div>
        )}
      </div>
      {canEdit && (
        <button
          className="btn btn-outline btn-sm"
          onClick={onEdit}
          title="Edit contact"
        >
          Edit
        </button>
      )}
    </div>
  );
}

function ContactEditor({
  customerId,
  mode,
  contact,
  onCancel,
  onSaved,
  onDelete,
}: {
  customerId: string;
  mode: 'create' | 'edit';
  contact?: Contact;
  onCancel: () => void;
  onSaved: () => void;
  onDelete?: () => void | Promise<void>;
}) {
  const [firstName, setFirstName] = useState(contact?.firstName ?? '');
  const [lastName, setLastName] = useState(contact?.lastName ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [jobTitle, setJobTitle] = useState(contact?.jobTitle ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [preferredChannel, setPreferredChannel] = useState(
    contact?.preferredChannel ?? '',
  );
  const [operationalRoles, setOperationalRoles] = useState<string[]>(
    contact?.operationalRoles ?? [],
  );
  const [salesRole, setSalesRole] = useState<string>(contact?.salesRole ?? '');
  const [isPrimary, setIsPrimary] = useState(contact?.isPrimary ?? false);
  const [isDecisionMaker, setIsDecisionMaker] = useState(
    contact?.isDecisionMaker ?? false,
  );
  const [status, setStatus] = useState<ContactStatus>(
    (contact?.status as ContactStatus) ?? 'active',
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleOp = (r: string) => {
    setOperationalRoles((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r],
    );
  };

  const save = async () => {
    try {
      setSaving(true);
      setErr(null);
      const body = {
        firstName: firstName || null,
        lastName: lastName || null,
        email: email || null,
        jobTitle: jobTitle || null,
        phone: phone || null,
        preferredChannel: preferredChannel || null,
        operationalRoles,
        salesRole: salesRole || null,
        isPrimary,
        isDecisionMaker,
        status,
      };
      if (mode === 'create') {
        await apiFetch(
          `/api/customers/${encodeURIComponent(customerId)}/contacts`,
          {
            method: 'POST',
            body: JSON.stringify(body),
          },
        );
      } else if (contact) {
        await apiFetch(
          `/api/customers/${encodeURIComponent(customerId)}/contacts/${contact.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify(body),
          },
        );
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        padding: 14,
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-card)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      <div className="form-grid" style={{ marginBottom: 10 }}>
        <Field label="First name">
          <input
            className="input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Field>
        <Field label="Last name">
          <input
            className="input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Field>
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Job title">
          <input
            className="input"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <input
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <select
            className="input"
            value={status}
            onChange={(e) => setStatus(e.target.value as ContactStatus)}
          >
            {CONTACT_STATUSES.filter((s) => s !== 'merged').map((s) => (
              <option key={s} value={s}>
                {CONTACT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Preferred contact method">
          <select
            className="input"
            value={preferredChannel}
            onChange={(e) => setPreferredChannel(e.target.value)}
          >
            <option value="">No preference / unknown</option>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            fontSize: 'var(--fs-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            marginBottom: 6,
          }}
        >
          Operational roles (our taxonomy — pick any that apply)
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {OPERATIONAL_ROLES.map((r) => {
            const on = operationalRoles.includes(r);
            return (
              <button
                key={r}
                type="button"
                onClick={() => toggleOp(r)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: on
                    ? '1px solid var(--accent)'
                    : '1px solid var(--border)',
                  background: on ? 'var(--accent)' : 'var(--bg-card)',
                  color: on ? '#fff' : 'var(--text-secondary)',
                  fontFamily: 'inherit',
                }}
              >
                {OPERATIONAL_ROLE_LABEL[r]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="form-grid" style={{ marginBottom: 10 }}>
        <Field label="HubSpot sales role (read-only mirror)">
          <select
            className="input"
            value={salesRole}
            onChange={(e) => setSalesRole(e.target.value)}
          >
            <option value="">(none)</option>
            {SALES_ROLES.map((s) => (
              <option key={s} value={s}>
                {SALES_ROLE_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        <CheckField
          label="Primary contact for this customer"
          value={isPrimary}
          onChange={setIsPrimary}
        />
        <CheckField
          label="Decision maker"
          value={isDecisionMaker}
          onChange={setIsDecisionMaker}
        />
      </div>

      {err && (
        <div className="banner banner-error" style={{ marginBottom: 8 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        {onDelete && (
          <button
            className="btn btn-outline btn-sm"
            style={{ color: '#B22B2B' }}
            onClick={() => void onDelete()}
            disabled={saving}
          >
            Delete
          </button>
        )}
        <button
          className="btn btn-outline btn-sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Add contact' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function CheckField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}
    >
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span style={{ fontSize: 'var(--fs-base)' }}>{label}</span>
    </label>
  );
}

function Chip({
  children,
  bg,
  color,
  border,
}: {
  children: React.ReactNode;
  bg: string;
  color: string;
  border: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        background: bg,
        color,
        border: `1px solid ${border}`,
        borderRadius: 999,
        fontSize: 'var(--fs-xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </span>
  );
}
